import { useCallback, useEffect, useMemo } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import getLang from 'LearningCenterUI/utils/getLang';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIFlex from 'UIComponents/layout/UIFlex';

// @ts-expect-error Could not find a declaration file for module
import { useSearchParam } from '../hooks/searchParamHooks';
import UsageTracker from '../utils/UsageTracker';
import { SCREENS } from '../constants/Tracking';
import { fetchLessonIfNeeded
// @ts-expect-error Could not find a declaration file for module
} from '../actions/LessonActions';
import { getContentTypeForTracking } from '../utils/ContentUtils';
import LessonPage from '../components/LessonPage/LessonPage';
import { getLesson
// @ts-expect-error Could not find a declaration file for module
} from '../selectors/lessonSelectors';
import ApiError from '../components/Errors/ApiError';
// @ts-expect-error Could not find a declaration file for module
import LearningPageHeader from '../containers/TrackPage/LearningPageHeader';
// @ts-expect-error Could not find a declaration file for module
import LessonNavigatorContainer from '../containers/Navigator/LessonNavigator/ExpandedLessonNavigatorLessonContainer';
import LearningPageSidebar from '../containers/TrackPage/LearningPageSidebar';
import LearningPageMainSection from '../containers/TrackPage/LearningPageMainSection';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FILTER_TYPES } from '../constants/TopFilterConstants';
import { useFullLesson } from '../features/lessons';
import { useLessonProgressById } from '../features/userProgress';
import maxBy from 'hs-lodash/maxBy';
import findIndex from 'hs-lodash/findIndex';
import { TRACK_NEXT_IN_PLAYLIST_URL_FRAGMENT, TRACK_PLAYLIST_COMPLETE_URL_FRAGMENT, TRACK_PLAYLIST_INCOMPLETE_URL_FRAGMENT, TRACK_RECOMMENDATIONS_URL_FRAGMENT } from '../constants/Navigation';
import { HasParentPlaylist, NextPlaylistContent } from '../features/playlistContent';
import { useAvailableLanguageOrFallback } from '../features/contentLanguage';
import useWootricSurvey from '../hooks/useWootricSurvey';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StickyDiv = styled.div.withConfig({
  displayName: "Lesson__StickyDiv",
  componentId: "m0ooll-0"
})(["position:sticky;top:49px;z-index:2;"]);
const useCurrentLanguage = lesson => {
  const [[paramLanguage]] = useSearchParam(FILTER_TYPES.LANGUAGE);
  const selectedLessonLanguage = lesson ? lesson.get('language') || getLang() : getLang();
  return paramLanguage || selectedLessonLanguage;
};
const noRedirectRoutes = [TRACK_NEXT_IN_PLAYLIST_URL_FRAGMENT, TRACK_PLAYLIST_COMPLETE_URL_FRAGMENT, TRACK_PLAYLIST_INCOMPLETE_URL_FRAGMENT, TRACK_RECOMMENDATIONS_URL_FRAGMENT];
export const useLessonRedirectUrl = (lessonId, language, contentId) => {
  const {
    lessonProgressById,
    lessonProgressByIdError,
    lessonProgressByIdLoading
  } = useLessonProgressById(lessonId);
  const {
    lesson,
    lessonError,
    lessonLoading
  } = useFullLesson(lessonId, language);
  const {
    location
  } = useHistory();
  const lessonRedirectUrl = useMemo(() => {
    if (!lessonProgressById || !lesson) return null;

    // We never want to redirect from the pages shown at the end of a lesson
    for (const route of noRedirectRoutes) {
      if (location.pathname.includes(route)) return null;
    }
    if (contentId) {
      if (!lesson.content.some(content => content.id === contentId)) return `/lessons/${lessonId}`;else return null;
    }
    const allContentProgress = [];
    lesson.content.forEach(content => {
      const contentProgress = lessonProgressById.contentProgress.find(c => c.contentId === content.id);
      allContentProgress.push({
        contentId: content.id,
        status: contentProgress ? contentProgress.progressType : 'NOT_STARTED',
        updatedAt: contentProgress ? contentProgress.updatedAt : null,
        url: `/lessons/${lessonId}/${content.id}`
      });
    });
    allContentProgress.push({
      contentId: null,
      status: null,
      updatedAt: null,
      url: `/lessons/${lessonId}/recommendations`
    });
    if (lessonProgressById.status === 'COMPLETED') {
      return allContentProgress[allContentProgress.length - 1].url;
    }
    const lastCompleted = maxBy(allContentProgress.filter(p => p.status === 'COMPLETED'), p => p.updatedAt);
    if (lastCompleted) {
      const lastCompletedIndex = findIndex(allContentProgress, lastCompleted);
      const unprogressedItemsAfterLastCompletedItem = allContentProgress.slice(lastCompletedIndex + 1).filter(p => p.status !== 'COMPLETED');
      return unprogressedItemsAfterLastCompletedItem[0].url;
    }
    return allContentProgress[0].url;
  }, [contentId, lesson, lessonId, lessonProgressById, location.pathname]);
  return {
    lessonRedirectUrl,
    lessonRedirectUrlLoading: lessonProgressByIdLoading || lessonLoading,
    lessonRedirectUrlError: lessonProgressByIdError || lessonError
  };
};
const getNextContentId = (lesson, contentId) => {
  if (!lesson || !lesson.get('contentIds') || !contentId) return null;
  const contentIds = lesson.get('contentIds');
  const currentIndex = contentIds.indexOf(contentId);
  return contentIds.get(currentIndex + 1, null);
};
const Lesson = () => {
  useWootricSurvey('CSAT');
  const dispatch = useDispatch();
  const {
    push,
    location
  } = useHistory();
  const contentById = useSelector(state => state.contentById);
  const {
    contentId,
    lessonId
  } = useParams();
  const content = contentId ? contentById.get(contentId) : null;
  const contentName = content && content.get('name');
  const contentType = getContentTypeForTracking(content);
  const lesson = useSelector(state => getLesson(state, {
    lessonId
  }));
  const language = useCurrentLanguage(lesson);
  const {
    lessonRedirectUrl,
    lessonRedirectUrlLoading
  } = useLessonRedirectUrl(Number(lessonId), language, Number(contentId));
  const nextContentId = getNextContentId(lesson, contentId);
  const handleAdvanceContent = useCallback(() => {
    if (nextContentId) {
      push(Object.assign({}, location, {
        pathname: `/lessons/${lesson.get('id')}/${nextContentId}`
      }));
    } else {
      push(Object.assign({}, location, {
        pathname: `recommendations`
      }));
    }
  }, [push, location, lesson, nextContentId]);
  const trackResourceClick = useCallback(({
    title,
    url
  }) => {
    UsageTracker.track('contentInteraction', {
      action: 'open-resource-link',
      contentId,
      contentLanguage: language,
      contentType,
      lessonId: lesson.get('id'),
      lessonName: lesson.get('name'),
      resourceTitle: title,
      resourceUrl: url
    });
  }, [language, lesson, contentId, contentType]);
  const {
    languageToLoad,
    availableLanguagesLoading
  } = useAvailableLanguageOrFallback({
    lessonId: Number(lessonId),
    language
  });
  useEffect(() => {
    if (!availableLanguagesLoading) dispatch(fetchLessonIfNeeded(lessonId, languageToLoad));
  }, [lessonId, languageToLoad, dispatch, availableLanguagesLoading]);
  useEffect(() => {
    if (lesson && lesson.get('name')) {
      UsageTracker.track('pageView', {
        screen: SCREENS.LESSON,
        contentId,
        contentType,
        lessonId,
        lessonName: lesson.get('name'),
        hsSoftwareFilters: lesson.get('hsSoftwareFilters', []).join(', '),
        allCategories: lesson.get('subCategories', []).join(', ')
      });
    }
  }, [contentId, contentType, lesson, lessonId]);
  if (lesson && lesson.get('hasError')) {
    return /*#__PURE__*/_jsx(ApiError, {
      className: "lesson-api-error",
      error: lesson.get('recentError'),
      marker404: "LESSON_VIEW_API_404",
      markerDefault: "LESSON_VIEW_API_ERROR"
    });
  }
  const isLoading = !lesson || lessonRedirectUrlLoading || availableLanguagesLoading || lesson.get('language', '').toUpperCase() !== language && lesson.get('isRequesting');
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(StickyDiv, {
      children: !isLoading && /*#__PURE__*/_jsx(LearningPageHeader, {
        ContentName: contentType === 'quiz' ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.lessonNavigator.quiz"
        }) : contentName,
        defaultBackLink: {
          pathname: '/',
          search: ''
        },
        language: language,
        lessonId: lessonId
      })
    }), isLoading ? /*#__PURE__*/_jsx(UILoadingSpinner, {
      grow: true,
      minHeight: 200
    }) : /*#__PURE__*/_jsx(NavMarker, {
      name: "LESSON_VIEW_LOADED",
      children: /*#__PURE__*/_jsxs(UIFlex, {
        children: [/*#__PURE__*/_jsx(LearningPageSidebar, {
          id: "sidebarNavigator",
          withSubNav: 1,
          children: /*#__PURE__*/_jsx(LessonNavigatorContainer, {})
        }), /*#__PURE__*/_jsx(LearningPageMainSection, {
          children: /*#__PURE__*/_jsx(HasParentPlaylist, {
            fallback: lesson.get('inactiveRedirectUrl') || lessonRedirectUrl ? /*#__PURE__*/_jsx(Redirect, {
              to: {
                pathname: lesson.get('inactiveRedirectUrl') || lessonRedirectUrl,
                search: window.location.search
              }
            }) : /*#__PURE__*/_jsx(LessonPage, {
              contentId: contentId,
              lesson: lesson,
              onAdvanceContent: handleAdvanceContent,
              onResourceClick: trackResourceClick
            }),
            children: /*#__PURE__*/_jsx(NextPlaylistContent, {
              lessonId: lesson.get('id'),
              redirectUrl: lessonRedirectUrl || undefined,
              render: ({
                navigate,
                url
              }) => !isLoading && lessonRedirectUrl ? /*#__PURE__*/_jsx(Redirect, {
                to: Object.assign({}, location, {
                  pathname: url
                })
              }) : /*#__PURE__*/_jsx(LessonPage, {
                contentId: contentId,
                lesson: lesson,
                onAdvanceContent: nextContentId ? handleAdvanceContent : navigate,
                onResourceClick: trackResourceClick
              })
            })
          })
        })]
      })
    })]
  });
};
export default Lesson;